import type { MetadataItem } from '../tool-config-types';
import { LiveDot } from '../LiveDot';

type InteractshOutput = {
  mode?: 'domain-generated' | 'interactions-check' | 'dynamic-response';
  url?: string;
  domain?: string;
  dynamicUrl?: string;
  status?: string;
  interactions?: unknown[];
  detections?: unknown[];
  results?: unknown[];
  count?: number;
  detected?: boolean;
};

type MetadataExtractorContext = {
  input: unknown;
  output?: unknown;
  status?: string;
};

const isInteractshOutput = (output: unknown): output is InteractshOutput =>
  typeof output === 'object' && output !== null;

const getInteractionCount = (output: InteractshOutput): number =>
  output.count ??
  output.interactions?.length ??
  output.detections?.length ??
  output.results?.length ??
  0;

const formatHits = (count: number): string =>
  `${count} ${count === 1 ? 'hit' : 'hits'}`;

const MODE_METADATA: Record<
  string,
  (output: InteractshOutput) => MetadataItem[]
> = {
  'domain-generated': () => [{ value: 'Listening', icon: LiveDot() }],
  'dynamic-response': () => [{ value: 'Configured' }],
  'interactions-check': (output) => {
    const count = getInteractionCount(output);
    return [
      { value: formatHits(count), color: count > 0 ? 'green' : undefined },
    ];
  },
};

const inferMetadataFromShape = (output: InteractshOutput): MetadataItem[] => {
  const count = getInteractionCount(output);

  const fallbacks: Array<{
    test: () => boolean;
    result: () => MetadataItem[];
  }> = [
    {
      test: () => count > 0,
      result: () => [{ value: formatHits(count), color: 'green' }],
    },
    {
      test: () => output.detected === true,
      result: () => [{ value: 'Detected', color: 'green' }],
    },
    {
      test: () => output.detected === false,
      result: () => [{ value: 'No hits' }],
    },
    {
      test: () => Boolean(output.url || output.domain),
      result: () => [{ value: 'Listening', icon: LiveDot() }],
    },
  ];

  return fallbacks.find((f) => f.test())?.result() ?? [];
};

export const extractInteractshMetadata = ({
  output,
}: MetadataExtractorContext): MetadataItem[] => {
  if (!isInteractshOutput(output)) return [];

  const modeResolver = output.mode ? MODE_METADATA[output.mode] : undefined;
  return modeResolver?.(output) ?? inferMetadataFromShape(output);
};
